import type {
  Medium,
  MediumDateiTokenResponse,
  MediumListResponse,
  MediumUploadFelder,
  MediumVariante,
  UpdateMediumRequest,
} from '@brandschutz/shared';
import { api } from '@/lib/api';

/**
 * Medien — Fotos, Videos und Unterlagen aus dem Betrieb.
 *
 * Die Auslieferung braucht kein `fetch` samt Objekt-URL mehr: Ein kurzlebiges
 * Datei-Token macht aus der Adresse eine, die ein nacktes `<img src>` laden
 * darf. Das Token wird zwischengespeichert, bis es abläuft — eine Karte mit
 * dreißig Vorschaubildern holt es einmal je Medium, nicht dreißigmal.
 */

export interface MediumListOptions {
  liegenschaftId?: number;
  anlageId?: number;
  mangelId?: number;
  /** Mehrere Befunde auf einmal — die Karte fragt für ihre Zeilen gemeinsam. */
  mangelIds?: number[];
  begehungId?: number;
  pruefungId?: number;
  aufgabeErledigungId?: number;
  projectId?: number;
  zweck?: string;
  art?: Medium['art'];
  status?: Medium['status'];
  limit?: number;
  offset?: number;
}

export async function listMedien(opts: MediumListOptions = {}): Promise<MediumListResponse> {
  const { mangelIds, ...rest } = opts;
  const { data } = await api.get<MediumListResponse>('/medien', {
    params: { ...rest, mangelIds: mangelIds?.length ? mangelIds.join(',') : undefined },
  });
  return data;
}

/** So viele Medien holt ein Aufruf höchstens — der Server deckelt bei 500. */
const SEITE = 500;

/**
 * Alle Treffer einer Abfrage, über Seiten hinweg.
 *
 * `total` sagt, wie viele es gibt; ohne diese Schleife fehlten ab der ersten
 * vollen Seite Bilder, ohne dass die Oberfläche es merkt. Der Deckel von zehn
 * Seiten ist eine Reißleine gegen eine Endlosschleife, falls sich der Bestand
 * während des Blätterns ändert.
 */
export async function listMedienAlle(opts: MediumListOptions = {}): Promise<MediumListResponse> {
  const items: Medium[] = [];
  let total = 0;
  for (let seite = 0; seite < 10; seite += 1) {
    const antwort = await listMedien({ ...opts, limit: SEITE, offset: seite * SEITE });
    items.push(...antwort.items);
    total = antwort.total;
    if (antwort.items.length < SEITE || items.length >= total) break;
  }
  return { items, total };
}

export async function getMedium(id: string): Promise<Medium> {
  const { data } = await api.get<Medium>(`/medien/${id}`);
  return data;
}

/** Felder des Uploads — der Zweck ist Pflicht, mindestens eine Kante auch. */
export type MediumUploadFelderEingabe = Partial<MediumUploadFelder> & { zweck: string };

export async function uploadMedium(
  file: File,
  felder: MediumUploadFelderEingabe,
  onProgress?: (prozent: number) => void,
): Promise<Medium> {
  const form = new FormData();
  form.append('file', file, file.name);
  const params: Record<string, string> = {};
  for (const [key, wert] of Object.entries(felder)) {
    if (wert !== undefined && wert !== null) params[key] = String(wert);
  }
  const { data } = await api.post<Medium>('/medien', form, {
    params,
    headers: { 'Content-Type': 'multipart/form-data' },
    // Ein Video vom Handy darf länger brauchen als der übliche Aufruf.
    timeout: 300_000,
    onUploadProgress: (e) => {
      if (!onProgress || !e.total) return;
      onProgress(Math.round((e.loaded / e.total) * 100));
    },
  });
  return data;
}

export async function updateMedium(id: string, payload: UpdateMediumRequest): Promise<Medium> {
  const { data } = await api.patch<Medium>(`/medien/${id}`, payload);
  return data;
}

/** Löschen ist ein Soft-Delete — die Datei bleibt eine Frist lang liegen. */
export async function deleteMedium(id: string): Promise<void> {
  await api.delete(`/medien/${id}`);
}

// ------------------------------------------------------------- Datei-Adressen

interface TokenEintrag {
  token: string;
  /** Millisekunden-Zeitstempel, ab dem das Token nicht mehr genutzt wird. */
  gueltigBis: number;
}

const tokenCache = new Map<string, TokenEintrag>();

/** Sicherheitsabstand, damit ein Bild nicht mitten im Laden ungültig wird. */
const TOKEN_PUFFER_MS = 30_000;

export async function mediumDateiToken(id: string): Promise<string> {
  const vorhanden = tokenCache.get(id);
  if (vorhanden && vorhanden.gueltigBis - TOKEN_PUFFER_MS > Date.now()) return vorhanden.token;
  const { data } = await api.post<MediumDateiTokenResponse>(`/medien/${id}/datei-token`);
  tokenCache.set(id, { token: data.token, gueltigBis: new Date(data.gueltigBis).getTime() });
  return data.token;
}

/**
 * Adresse einer Fassung, die ein `<img src>` oder `<video src>` direkt laden
 * kann. Der Aufrufer wartet auf die Zusage — ohne Token antwortet die Route
 * mit 401, weil ein Tag keinen Bearer-Header schickt.
 */
export async function mediumDateiUrl(id: string, variante: MediumVariante): Promise<string> {
  const token = await mediumDateiToken(id);
  return `/api/v1/medien/${id}/datei/${variante}?t=${encodeURIComponent(token)}`;
}

/**
 * Gemerkte Datei-Token vergessen.
 *
 * Wird beim Abmelden aufgerufen (lib/auth-context.tsx): Ein Datei-Token lebt
 * zehn Minuten und prüft nur Zweck und Medium, nicht die Person. Auf einem
 * geteilten Gerät hätte die nächste Person sonst noch gültige Bild-Adressen
 * in der Hand.
 */
export function leereMedienTokenCache(): void {
  tokenCache.clear();
}
